import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import type { Dispatch, JSX, ReactNode, SetStateAction } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { useAccountId } from '~publish/contexts/AccountContext'
import type { CreateNoteResult } from '~publish/hooks/notes/useCreateNote'
import type { UpdateNoteResult } from '~publish/hooks/notes/useUpdateNote'
import type { NotesResult } from '~publish/hooks/notes/useNotes'
import { useReactToNote } from '~publish/hooks/notes/useReactToNote'
import type { DeleteNoteResult } from '~publish/hooks/notes/useDeleteNote'
import type { CollaborationNote } from '~publish/hooks/notes/notesQueries'
import type { CreatedNote } from '~publish/hooks/notes/notesQueries'
import { noteSubjectKey } from '~publish/hooks/notes/noteSubject'
import type { NoteSubject } from '~publish/hooks/notes/noteSubject'
import type { NoteContent } from '~publish/components/Notes'
import { canEditNoteAnnotations } from '~publish/hooks/notes/noteContent'
import type { TeamMemberScope } from '~publish/hooks/notes/selectableTeamMembersQuery'
import { notesDraftIsDirty } from '~publish/hooks/notes/notesDismiss'
import { useOptionalNotesDismissGuard } from '~publish/hooks/notes/NotesDismissGuard'
import {
  getFrequentlyUsedEmojis,
  updateEmojiFrequency,
} from '~publish/helpers/emojiFrequency'

import {
  NOTE_REACTION_EMOJI_STORE,
  QUICK_REACTION_COUNT,
  quickReactions,
} from '~publish/components/PostNotes/noteReactionDisplay'

import type {
  CollaborationNotesAdapter,
  CreateNoteOperation,
  NotesReadStateOperation,
  UpdateNoteOperation,
} from './collaborationNotesAdapter'

type EditDraft = { noteId: string; content: NoteContent }
const emptyContent = (): NoteContent => ({ text: '', annotations: [] })

/** Unsent composer drafts, keyed by selection key. */
export type ComposerDrafts = Record<
  string,
  { content: NoteContent; revision: number }
>

/** A composer draft store and its setter, as `useState` returns them. */
export type ComposerDraftStore = readonly [
  ComposerDrafts,
  Dispatch<SetStateAction<ComposerDrafts>>,
]

/**
 * A composer draft store a host can own, so an unsent draft outlives the
 * provider, for example while the host shows another thread in its place.
 */
export function useCollaborationNotesComposerDrafts(): ComposerDraftStore {
  return useState<ComposerDrafts>({})
}

const recordNoteReactionUse = (emoji: string): void =>
  updateEmojiFrequency(emoji, NOTE_REACTION_EMOJI_STORE)

const noReadAction: NotesReadStateOperation['markRead'] = {
  submit: async () => false,
  pending: false,
  error: null,
}

/** Read state for a subject that has none: idle, with inert actions. */
const noReadState: NotesReadStateOperation = {
  readState: {
    status: 'idle',
    state: null,
    apply: () => undefined,
    refresh: async () => undefined,
    retry: async () => undefined,
  },
  markRead: noReadAction,
  markUnread: noReadAction,
}

export type CollaborationNotesContextValue = {
  /** Account, subject type, and subject ID; `null` with no account. */
  selectionKey: string | null
  mentionScope: TeamMemberScope | null
  /** The thread takes no new Notes. Existing Notes stay editable. */
  closed: boolean
  notes: NotesResult
  /** The subject keeps a read position. When false, the read state stays idle. */
  tracksReadState: boolean
  readState: NotesReadStateOperation['readState']
  autoReadEnabled: boolean
  markRead: NotesReadStateOperation['markRead']
  markUnread: NotesReadStateOperation['markUnread']
  create: {
    draft: NoteContent
    revision: number
    changeDraft: (content: NoteContent) => void
    clearSubmittedDraft: (content: NoteContent) => void
    pending: boolean
    error: CreateNoteOperation['error']
    confirmedNotes: readonly CreatedNote[]
    enabled: boolean
    submit: (content: NoteContent) => Promise<CreateNoteResult>
  }
  edit: {
    draft: EditDraft | null
    pending: boolean
    enabled: boolean
    errorFor: UpdateNoteOperation['errorFor']
    start: (note: CollaborationNote) => void
    change: (content: NoteContent) => void
    cancel: () => void
    submit: (
      note: CollaborationNote,
      content: NoteContent,
    ) => Promise<UpdateNoteResult>
  }
  remove: {
    pending: boolean
    enabled: boolean
    submit: (note: CollaborationNote) => Promise<DeleteNoteResult>
  }
  react: {
    toggle: (note: CollaborationNote, emoji: string) => Promise<void>
    /**
     * Read once for the whole feed, so every Note offers the same choices and
     * they do not reorder under the pointer after a reaction.
     */
    quickReactions: readonly string[]
  }
}

const CollaborationNotesContext =
  createContext<CollaborationNotesContextValue | null>(null)

export function useCollaborationNotesContext(): CollaborationNotesContextValue {
  const context = useContext(CollaborationNotesContext)
  if (!context) {
    throw new Error('Notes parts need a CollaborationNotesProvider')
  }
  return context
}

/**
 * The state behind one Notes thread: composer and edit drafts, pending
 * writes, errors, and which controls are on. Drafts are kept per account and
 * subject, so switching threads and back restores them.
 *
 * It knows nothing about the entity a thread hangs off. The host passes the
 * subject, an adapter with the subject's operations, and what the account may
 * do there.
 */
export function CollaborationNotesProvider<Subject extends NoteSubject>({
  subject,
  adapter,
  canCreate,
  isClosed,
  focusAfterDelete,
  mentionScope,
  onThreadClosed,
  composerDrafts,
  children,
}: {
  subject: Subject
  /** Must stay the same for the provider's lifetime. */
  adapter: CollaborationNotesAdapter<Subject>
  /** The account may add Notes to this subject. */
  canCreate: boolean
  /** The thread no longer takes new Notes, whatever the account may do. */
  isClosed: boolean
  focusAfterDelete: () => void
  mentionScope: TeamMemberScope | null
  /**
   * The server rejected a create because the thread closed. The host owns
   * the thread state, so it closes the thread without waiting for a refetch.
   */
  onThreadClosed?: () => void
  /**
   * A host-owned composer draft store, from
   * `useCollaborationNotesComposerDrafts`. Without it the provider keeps its
   * own, and the drafts go when it unmounts.
   */
  composerDrafts?: ComposerDraftStore
  children: ReactNode
}): JSX.Element {
  const accountId = useAccountId()
  const client = useApolloClient()
  const dismissGuard = useOptionalNotesDismissGuard()
  const selectionKey = noteSubjectKey(accountId, subject)
  const createAllowed = canCreate && !isClosed
  const ownDrafts = useCollaborationNotesComposerDrafts()
  const [drafts, setDrafts] = composerDrafts ?? ownDrafts
  const clearWarn = dismissGuard?.clearWarn
  const setHasUnsavedDraft = dismissGuard?.setHasUnsavedDraft
  const changeDraft = useCallback(
    (content: NoteContent): void => {
      clearWarn?.()
      setDrafts((current) => ({
        ...current,
        [selectionKey]: {
          content,
          revision: current[selectionKey]?.revision ?? 0,
        },
      }))
    },
    [clearWarn, selectionKey, setDrafts],
  )
  const clearSubmittedDraft = useCallback(
    (content: NoteContent): void => {
      setDrafts((current) =>
        current[selectionKey]?.content === content
          ? {
              ...current,
              [selectionKey]: {
                content: emptyContent(),
                revision: current[selectionKey].revision + 1,
              },
            }
          : current,
      )
    },
    [selectionKey, setDrafts],
  )
  const [editDrafts, setEditDrafts] = useState<Record<string, EditDraft>>({})
  const create = adapter.useCreateNote(subject, createAllowed)
  const update = adapter.useUpdateNote(subject)
  const remove = adapter.useDeleteNote(subject)
  const [quickReactionEmojis] = useState(() =>
    quickReactions(
      getFrequentlyUsedEmojis(QUICK_REACTION_COUNT, NOTE_REACTION_EMOJI_STORE),
    ),
  )
  const react = useReactToNote({ onAdd: recordNoteReactionUse })
  const notes = adapter.useNotes(
    subject,
    create.pending || update.pending || remove.pending,
  )
  adapter.useSubscribe(subject)
  const readOperation = adapter.useReadState(subject)
  const { readState, markRead, markUnread } = readOperation ?? noReadState
  // Manual unread disables auto-read for this account/subject viewing session.
  const [manualUnreadKey, setManualUnreadKey] = useState<string | null>(null)
  useEffect(() => setManualUnreadKey(null), [selectionKey])
  const submitMarkUnread = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!accountId || markRead.pending || markUnread.pending) return false
      const previousState = readState.state
      const previouslyManual = manualUnreadKey === selectionKey
      setManualUnreadKey(selectionKey)
      const index =
        notes.status === 'ready'
          ? notes.notes.findIndex((note) => note.id === noteId)
          : -1
      if (previousState && notes.status === 'ready' && index >= 0) {
        // Newest-first ordering makes index + 1 the optimistic unread count.
        readState.apply({
          ...previousState,
          readThroughNoteId:
            notes.notes[index + 1]?.id ??
            (notes.hasNextPage ? previousState.readThroughNoteId : null),
          firstUnreadNoteId: noteId,
          unreadNoteCount: index + 1,
        })
      }
      const succeeded = await markUnread.submit(noteId)
      if (!succeeded) {
        if (previousState) readState.apply(previousState)
        // A failed retry must preserve an earlier manual-unread action.
        if (!previouslyManual) {
          setManualUnreadKey((current) =>
            current === selectionKey ? null : current,
          )
        }
      }
      return succeeded
    },
    [
      accountId,
      manualUnreadKey,
      markRead.pending,
      markUnread,
      notes,
      readState,
      selectionKey,
    ],
  )
  useEffect(() => {
    if (notes.status === 'ready') {
      create.retireConfirmedNotes(notes.notes.map((note) => note.id))
    }
  }, [create, notes])
  const submit = useCallback(
    async (content: NoteContent): Promise<CreateNoteResult> => {
      if (notes.status !== 'ready' || !notes.beginWrite()) {
        create.failRequest()
        return { ok: false, reason: 'request' }
      }
      let result: CreateNoteResult
      try {
        result = await create.submit(content)
      } finally {
        notes.endWrite()
      }
      if (!result.ok && result.reason === 'closed') onThreadClosed?.()
      if (result.ok && create.isCurrentSelection()) {
        adapter.insertCreatedNote(client.cache, subject, result.edge)
      }
      return result
    },
    [adapter, client, create, notes, onThreadClosed, subject],
  )
  const startEdit = useCallback(
    (note: CollaborationNote): void => {
      if (
        !accountId ||
        !note.allowedActions.includes('updateNote') ||
        !canEditNoteAnnotations(note.text, note.annotations)
      )
        return
      update.clearError(note.id)
      setEditDrafts((current) => ({
        ...current,
        [selectionKey]: {
          noteId: note.id,
          content: { text: note.text, annotations: [...note.annotations] },
        },
      }))
    },
    [accountId, selectionKey, update],
  )
  const changeEdit = useCallback(
    (content: NoteContent): void => {
      clearWarn?.()
      setEditDrafts((current) => {
        const draft = current[selectionKey]
        return draft
          ? { ...current, [selectionKey]: { ...draft, content } }
          : current
      })
    },
    [clearWarn, selectionKey],
  )
  const cancelEdit = useCallback((): void => {
    if (update.pending) return
    const draft = editDrafts[selectionKey]
    if (draft) update.clearError(draft.noteId)
    setEditDrafts((current) => {
      const next = { ...current }
      delete next[selectionKey]
      return next
    })
  }, [editDrafts, selectionKey, update])
  const submitEdit = useCallback(
    async (
      note: CollaborationNote,
      content: NoteContent,
    ): Promise<UpdateNoteResult> => {
      if (notes.status !== 'ready' || !notes.beginWrite()) {
        update.failRequest(note.id)
        return { ok: false, reason: 'request' }
      }
      try {
        const result = await update.submit(note, content)
        if (result.ok) {
          setEditDrafts((current) => {
            const draft = current[selectionKey]
            if (draft?.noteId !== note.id || draft.content !== content)
              return current
            const next = { ...current }
            delete next[selectionKey]
            return next
          })
        }
        return result
      } finally {
        notes.endWrite()
      }
    },
    [notes, selectionKey, update],
  )
  const createText = drafts[selectionKey]?.content.text ?? ''
  const isEditing = Boolean(editDrafts[selectionKey])
  const guardOwner = useId()
  useEffect(() => {
    setHasUnsavedDraft?.(notesDraftIsDirty(createText, isEditing), guardOwner)
    return (): void => setHasUnsavedDraft?.(false, guardOwner)
  }, [createText, guardOwner, isEditing, setHasUnsavedDraft])
  const submitDelete = useCallback(
    async (note: CollaborationNote): Promise<DeleteNoteResult> => {
      if (notes.status !== 'ready' || !notes.beginWrite()) {
        return { ok: false, reason: 'request' }
      }
      try {
        return await remove.submit(note, focusAfterDelete)
      } finally {
        notes.endWrite()
      }
    },
    [focusAfterDelete, notes, remove],
  )
  const contextValue = useMemo<CollaborationNotesContextValue>(
    () => ({
      selectionKey: accountId ? selectionKey : null,
      mentionScope,
      closed: isClosed,
      notes,
      tracksReadState: readOperation !== null,
      readState,
      autoReadEnabled: manualUnreadKey !== selectionKey,
      markRead,
      markUnread: { ...markUnread, submit: submitMarkUnread },
      create: {
        draft: drafts[selectionKey]?.content ?? emptyContent(),
        revision: drafts[selectionKey]?.revision ?? 0,
        changeDraft,
        clearSubmittedDraft,
        pending: create.pending,
        error: create.error,
        confirmedNotes: create.confirmedNotes,
        enabled:
          createAllowed &&
          notes.status === 'ready' &&
          !notes.readBusy &&
          !remove.pending,
        submit,
      },
      edit: {
        draft: editDrafts[selectionKey] ?? null,
        pending: update.pending,
        enabled: notes.status === 'ready' && !notes.readBusy && !remove.pending,
        errorFor: update.errorFor,
        start: startEdit,
        change: changeEdit,
        cancel: cancelEdit,
        submit: submitEdit,
      },
      remove: {
        pending: remove.pending,
        enabled:
          notes.status === 'ready' &&
          !notes.readBusy &&
          !create.pending &&
          !update.pending,
        submit: submitDelete,
      },
      react: { toggle: react.toggle, quickReactions: quickReactionEmojis },
    }),
    [
      accountId,
      cancelEdit,
      changeDraft,
      changeEdit,
      clearSubmittedDraft,
      create,
      createAllowed,
      drafts,
      editDrafts,
      isClosed,
      notes,
      readOperation,
      readState,
      markRead,
      markUnread,
      manualUnreadKey,
      submitMarkUnread,
      remove,
      mentionScope,
      quickReactionEmojis,
      react.toggle,
      selectionKey,
      startEdit,
      submit,
      submitEdit,
      submitDelete,
      update,
    ],
  )

  return (
    <CollaborationNotesContext.Provider value={contextValue}>
      {children}
    </CollaborationNotesContext.Provider>
  )
}
